import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, X, RefreshCw } from 'lucide-react';
import Disclaimer from './Disclaimer';

const CustomRulesModal = ({ isOpen, onClose, onSave, currentRules }) => {
  const [buyRules, setBuyRules] = useState([]);
  const [sellRules, setSellRules] = useState([]);
  const [newRule, setNewRule] = useState('');
  const [ruleType, setRuleType] = useState('buy');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [showFieldDropdown, setShowFieldDropdown] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState(null);
  const modalRef = useRef(null);
  const fieldDropdownRef = useRef(null);

  const availableFields = [
    'pe', 'rsi', 'macd', 'macd_signal', 'close', 'open', 'high', 'low', 'volume',
    'streak', 'pct_change', 'market_cap', 'sma10_prc', 'sma20_prc', 'sma50_prc',
    'sma90_prc', 'sma200_prc', 'ema20_prc', 'sma20_vol', 'pct_change_5days',
    'pct_change_10days', 'pct_change_20days', 'sma20_prc_diff_pct', 'sma20_vol_diff_pct',
    'high_prc_52weeks_pct_diff', 'low_prc_52weeks_pct_diff', 'high_prc_52weeks',
    'low_prc_52weeks', 'high_streak', 'prev_high_streak', 'bol_band20_low', 'bol_band20_upp',
    'status1', 'status2', 'last_action1', 'last_action2',
  ];

  const operators = ['>', '>=', '<', '<=', '=', '<>', '+', '-', '*', '/', 'BETWEEN'];

  const normalizeSavedRule = (rule) => {
    if (typeof rule !== 'string') return rule;
    return rule.replace(/\s==\s/g, ' = ').replace(/\s!=\s/g, ' <> ');
  };

  useEffect(() => {
    if (isOpen && currentRules) {
      setBuyRules((currentRules.buyRules || []).map(normalizeSavedRule));
      setSellRules((currentRules.sellRules || []).map(normalizeSavedRule));
    }
  }, [isOpen, currentRules]);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
    if (fieldDropdownRef.current && !fieldDropdownRef.current.contains(event.target)) {
      setShowFieldDropdown(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const validateRule = (rule) => {
    if (!rule.trim()) {
      setErrorMessage('Rule cannot be empty');
      return false;
    }
    if (/==/.test(rule)) {
      setErrorMessage('Use single = for equality (not ==)');
      return false;
    }
    if (/!=/.test(rule)) {
      setErrorMessage('Use <> for not equal (not !=)');
      return false;
    }
    const hasField = availableFields.some(field => rule.includes(field));
    const hasOperator = [...operators, 'AND', 'OR'].some(op => {
      if (op === '<>') return rule.includes('<>');
      return rule.includes(` ${op} `);
    });
    if (!hasField || !hasOperator) {
      setErrorMessage('Rule must include valid fields and comparison operators');
      return false;
    }
    const openParens = (rule.match(/\(/g) || []).length;
    const closeParens = (rule.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      setErrorMessage('Unbalanced parentheses in rule');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const addRule = () => {
    if (!validateRule(newRule)) return;
    if (editingRuleIndex !== null) {
      if (ruleType === 'buy') {
        const updatedRules = [...buyRules];
        updatedRules[editingRuleIndex] = newRule;
        setBuyRules(updatedRules);
      } else {
        const updatedRules = [...sellRules];
        updatedRules[editingRuleIndex] = newRule;
        setSellRules(updatedRules);
      }
      setEditingRuleIndex(null);
    } else {
      if (ruleType === 'buy') setBuyRules([...buyRules, newRule]);
      else setSellRules([...sellRules, newRule]);
    }
    setNewRule('');
  };

  const editRule = (index, type) => {
    const ruleToEdit = type === 'buy' ? buyRules[index] : sellRules[index];
    setNewRule(ruleToEdit);
    setRuleType(type);
    setEditingRuleIndex(index);
  };

  const removeRule = (index, type) => {
    if (type === 'buy') setBuyRules(buyRules.filter((_, i) => i !== index));
    else setSellRules(sellRules.filter((_, i) => i !== index));
    if (editingRuleIndex === index && ruleType === type) {
      setEditingRuleIndex(null);
      setNewRule('');
    }
  };

  const handleSave = () => {
    onSave({ buyRules, sellRules });
    onClose();
  };

  const addFieldToRule = (field) => {
    setNewRule(prev => prev + field);
    setShowFieldDropdown(false);
    setFieldSearchQuery('');
  };

  const addOperatorToRule = (operator) => {
    const formattedOperator = operator === 'BETWEEN' ? ' BETWEEN ' : ` ${operator} `;
    setNewRule(prev => prev + formattedOperator);
  };

  const filteredFields = fieldSearchQuery
    ? availableFields.filter(field =>
        field.toLowerCase().includes(fieldSearchQuery.toLowerCase())
      )
    : availableFields;

  const renderRuleHelp = () => (
    <div className="rule-help">
      <h4>Rule Syntax Help</h4>
      <p>Create conditions using fields, operators, and values:</p>
      <ul>
        <li><code>rsi {'>'} 70</code> - RSI greater than 70</li>
        <li><code>close {'>'} open</code> - Closing price greater than opening price</li>
        <li><code>volume {'>'} 1000000</code> - Volume greater than 1 million</li>
        <li><code>rsi BETWEEN 30 AND 70</code> - RSI between 30 and 70</li>
        <li><code>close / open {'>'} 1.05</code> - Close price is 5% higher than open</li>
        <li><code>(close - open) / open {'>'} 0.03</code> - Price increased by more than 3%</li>
        <li><code>status2 = buy</code> - Match default Momentum buy signal (use <code>status1</code> for Mean Reversion)</li>
        <li><code>rsi &lt;&gt; 70</code> - RSI is not equal to 70</li>
      </ul>
      <p>Combine conditions with AND/OR and parentheses:</p>
      <ul>
        <li><code>(rsi {'<'} 30) AND (volume {'>'} 500000)</code></li>
        <li><code>(close {'>'} sma20_prc) OR (streak {'>'} 3)</code></li>
        <li><code>((close + high) / 2 {'>'} sma50_prc) AND (volume {'>'} sma20_vol)</code></li>
      </ul>
      <p className="rule-note">Note: Use <code>=</code> for equal and <code>&lt;&gt;</code> for not equal (e.g. <code>status2 = buy</code>, <code>rsi &lt;&gt; 70</code>). Logical operators (AND, OR) are case-insensitive.</p>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal custom-rules-modal" ref={modalRef}>
        <div className="modal-content">
          <div className="modal-header">
            <h3>Custom Trading Rules</h3>
            <button onClick={onClose} className="modal-close">
              <X />
            </button>
          </div>
          <div className="modal-body">
            <div className="rule-tabs">
              <button
                className={`rule-tab ${ruleType === 'buy' ? 'active' : ''}`}
                onClick={() => {
                  setRuleType('buy');
                  if (editingRuleIndex !== null) { setEditingRuleIndex(null); setNewRule(''); }
                }}
              >
                Buy Rules
              </button>
              <button
                className={`rule-tab ${ruleType === 'sell' ? 'active' : ''}`}
                onClick={() => {
                  setRuleType('sell');
                  if (editingRuleIndex !== null) { setEditingRuleIndex(null); setNewRule(''); }
                }}
              >
                Sell Rules
              </button>
            </div>
            <div className="rule-section">
              <p className="rule-description">
                Buy rules determine when stocks will be labeled as "buy". Sell rules determine when stocks will be labeled as "sell".
                Stocks that don't match either buy or sell rules will be labeled as "hold".
              </p>
              <div className="rules-container">
                <div className="rule-category">
                  <h4 className="rule-category-title">Buy Rules</h4>
                  <div className="rule-list">
                    {buyRules.length > 0 ? (
                      buyRules.map((rule, index) => (
                        <div key={index} className="rule-item buy-rule">
                          <span className="rule-text">{rule}</span>
                          <div className="rule-actions">
                            <button className="edit-rule-button" onClick={() => editRule(index, 'buy')} title="Edit rule">
                              <RefreshCw size={16} />
                            </button>
                            <button className="remove-rule-button" onClick={() => removeRule(index, 'buy')} title="Remove rule">
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-rules-message">No buy rules defined yet.</p>
                    )}
                  </div>
                </div>
                <div className="rule-category">
                  <h4 className="rule-category-title">Sell Rules</h4>
                  <div className="rule-list">
                    {sellRules.length > 0 ? (
                      sellRules.map((rule, index) => (
                        <div key={index} className="rule-item sell-rule">
                          <span className="rule-text">{rule}</span>
                          <div className="rule-actions">
                            <button className="edit-rule-button" onClick={() => editRule(index, 'sell')} title="Edit rule">
                              <RefreshCw size={16} />
                            </button>
                            <button className="remove-rule-button" onClick={() => removeRule(index, 'sell')} title="Remove rule">
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-rules-message">No sell rules defined yet.</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="add-rule-section">
                <h4>{editingRuleIndex !== null ? 'Edit' : 'Add New'} {ruleType === 'buy' ? 'Buy' : 'Sell'} Rule</h4>
                <div className="operators-toolbar">
                  {operators.map(op => (
                    <span key={op} className="operator-tag" onClick={() => addOperatorToRule(op)}>{op}</span>
                  ))}
                  <span className="operator-tag" onClick={() => addOperatorToRule('AND')}>AND</span>
                  <span className="operator-tag" onClick={() => addOperatorToRule('OR')}>OR</span>
                  <span className="operator-tag" onClick={() => setNewRule(prev => prev + '(')}>( </span>
                  <span className="operator-tag" onClick={() => setNewRule(prev => prev + ')')}>)</span>
                </div>
                <div className="rule-input-container">
                  <textarea
                    value={newRule}
                    onChange={(e) => setNewRule(e.target.value)}
                    placeholder={`Enter a ${ruleType} rule (e.g., rsi < 30 AND volume > 1000000)`}
                    className="rule-input"
                    rows={3}
                  />
                  <div className="field-selector-container">
                    <div className="field-search-container">
                      <input
                        type="text"
                        placeholder="Search fields..."
                        value={fieldSearchQuery}
                        onChange={(e) => setFieldSearchQuery(e.target.value)}
                        onFocus={() => setShowFieldDropdown(true)}
                        className="field-search-input"
                      />
                      <button className="field-dropdown-toggle" onClick={() => setShowFieldDropdown(!showFieldDropdown)}>
                        {showFieldDropdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </div>
                    {showFieldDropdown && (
                      <div className="field-dropdown" ref={fieldDropdownRef}>
                        {filteredFields.length > 0 ? (
                          filteredFields.map(field => (
                            <div key={field} className="field-dropdown-item" onClick={() => addFieldToRule(field)}>
                              {field}
                            </div>
                          ))
                        ) : (
                          <div className="field-dropdown-item no-results">No matching fields</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="quick-access-fields">
                    {['rsi', 'macd', 'low', 'open', 'close', 'volume', 'pct_change', 'streak', 'sma20_prc', 'sma50_prc'].map(f => (
                      <span key={f} className="field-tag" onClick={() => addFieldToRule(f)}>{f}</span>
                    ))}
                  </div>
                  {errorMessage && <p className="rule-error">{errorMessage}</p>}
                  <button onClick={addRule} className="add-rule-button">
                    {editingRuleIndex !== null ? 'Update Rule' : 'Add Rule'}
                  </button>
                </div>
              </div>
              {renderRuleHelp()}
            </div>
          </div>
          <div className="modal-footer modal-footer--stacked">
            <Disclaimer variant="hypothetical" showTermsLink={false}>
              <strong>Backtesting only — not investment advice.</strong> Applying
              custom rules to past dates produces hypothetical results using
              historical data. They do not reflect actual trading and exclude
              fees, commissions, slippage, and taxes. Past or backtested
              performance does not guarantee future results.
            </Disclaimer>
            <button onClick={handleSave} className="save-rules-button">Save Rules</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomRulesModal;
